import React, { useState } from "react"

import { logCashAlertOverThreshold } from "../../../../../apis/Analytics/gtmActions"
import AlertModal from "../../../../../ui-components/AlertModal"
import { CHANGE_PAYMENT_METHOD } from "../../../../../stores/CartStateStore"
import SatispayForm from "./SatispayForm"
import StripeForm from "./StripeForm"
import CashForm from "./CashForm"

import styles from "./PaymentTypeSelector.module.scss"

const PAYMENTS_TABS = {
  CASH: 1,
  CREDIT_CARD: 2,
  SATISPAY: 3,
}

export default function PaymentTypeSelector(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    userAuthToken,
    showAlertMessage,
    showStripeMethod,
    isTakeAway,
    venueAcceptsCash,
    venueAcceptsStripe,
    venueAcceptsSatispay,
    amount,
    deliveryPrice,
    cartState,
    dispatchCartState,
    errors,
    stripe_key,
    cashSettings,
    wallet,
  } = props

  const maxCashAmountCents =
    cashSettings?.max_cash_amount_cents || Number.MAX_SAFE_INTEGER
  const maxChangeAmountCents = cashSettings?.max_change_amount_cents

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [tab, setTab] = useState(getDefaultTabIndex())
  const [error, setError] = useState(null)
  const [cashAmount, setCashAmount] = useState("")

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function isCashDisabledByAmount() {
    return amount > maxCashAmountCents && venueAcceptsStripe && !isTakeAway
  }

  function getDefaultTabIndex() {
    if (!venueAcceptsCash) {
      return PAYMENTS_TABS.CREDIT_CARD
    }

    if (wallet?.available_amount_cents > 0) {
      return PAYMENTS_TABS.CREDIT_CARD
    }

    if (isCashDisabledByAmount()) {
      return PAYMENTS_TABS.CREDIT_CARD
    }

    return PAYMENTS_TABS.CASH
  }

  function handleTabSelect(value) {
    switch (value) {
      case PAYMENTS_TABS.CASH:
        if (!venueAcceptsCash) {
          setError(I18n.t("cart_summary.venue_no_cash"))
          return
        }

        if (isCashDisabledByAmount()) {
          setError(
            I18n.t("cart_summary.cash_value_too_high_alert", {
              value: maxCashAmountCents / 100.0,
            })
          )
          logCashAlertOverThreshold()
          return
        }

        dispatchCartState({
          type: CHANGE_PAYMENT_METHOD,
          payload: { type: "cash", options: { cash_amount: cashAmount || 0 } },
        })
        break
      case PAYMENTS_TABS.CREDIT_CARD:
        // actually dispatched into StripeForm
        break
      case PAYMENTS_TABS.SATISPAY:
        if (!venueAcceptsSatispay) {
          setError(I18n.t("cart_summary.venue_no_satispay"))
          return
        }

        dispatchCartState({
          type: CHANGE_PAYMENT_METHOD,
          payload: { type: "satispay" },
        })
        break
    }

    setTab(value)
  }

  function renderCashForm() {
    return (
      <CashForm
        showAlertMessage={showAlertMessage}
        enabled={venueAcceptsCash}
        requiresCashValue={!isTakeAway}
        errors={errors}
        cartState={cartState}
        dispatchCartState={dispatchCartState}
        setCashAmount={setCashAmount}
        maxChangeAmountCents={maxChangeAmountCents}
      />
    )
  }

  function renderStripeForm() {
    return (
      <StripeForm
        userAuthToken={userAuthToken}
        stripe_key={stripe_key}
        shown={tab === PAYMENTS_TABS.CREDIT_CARD}
        showAlertMessage={showAlertMessage}
        deliveryPrice={deliveryPrice}
        venueAcceptsStripe={venueAcceptsStripe}
        cartState={cartState}
        dispatchCartState={dispatchCartState}
      />
    )
  }

  function renderSatispayForm() {
    return <SatispayForm />
  }

  function renderSelector(title, type, index) {
    return (
      <div
        aria-label={title}
        className={styles.selector}
        onClick={() => {
          handleTabSelect(index)
        }}
      >
        <input id={type} name="payment_type" value={type} type="radio" readOnly checked={tab === index} />
        <label htmlFor={type} className={`${styles.label} fr-p2 fr-normal fr-light fr-margin-left-small`}>
          {title}
        </label>
      </div>
    )
  }

  function renderSelectedForm() {
    switch (tab) {
      case PAYMENTS_TABS.CREDIT_CARD:
        return renderStripeForm()
      case PAYMENTS_TABS.SATISPAY:
        return renderSatispayForm()
      default:
        return renderCashForm()
    }
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  // -------------------------------------

  // GUARD
  if (!showStripeMethod) {
    return renderCashForm()
  }

  return (
    <div>
      <AlertModal
        onClose={() => setError(null)}
        show={!!error}
        title={I18n.t("cart_summary.warning")}
        text={error}
      />

      <div className={`${styles.selectorsContainer} fr-margin-top-large`}>
        {renderSelector(
          I18n.t("cart_summary.pay_with_cash"),
          "cash",
          PAYMENTS_TABS.CASH
        )}
        {renderSelector(
          I18n.t("cart_summary.pay_with_card"),
          "credit_card",
          PAYMENTS_TABS.CREDIT_CARD
        )}
        {renderSelector(
          I18n.t("cart_summary.pay_with_satispay"),
          "satispay",
          PAYMENTS_TABS.SATISPAY
        )}
      </div>

      <div>{renderSelectedForm()}</div>
    </div>
  )
}
